import { Box, Button, Paper, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getGpsrItemsRequest, getGpsrServiceStatusesRequest } from 'redux/reducers/gpsr/reducer';
import { PAGE_SIZE } from 'services/constants/env';
import { EasTableFilterPanel } from 'ui/EasTableFilterPanel';
import { EasTable } from 'ui/EasTable';
import { TGpsrItem } from 'redux/reducers/gpsr/types';
import {
	getGpsrItems,
	getGpsrItemsLoad,
	getGpsrServiceStatuses,
	getGpsrServiceStatusesLoad,
	getRegisterError,
} from 'redux/reducers/gpsr/selectors';
import { useLocation, useNavigate, useParams } from 'react-router';
import queryString from 'query-string';
import { PaginationBlock } from 'ui/PaginationBlock';
import { TFilters, TOrderBy } from 'redux/types';
import { DATES_ARRAY } from 'services/constants/dates-constants';
import { TTableStructure } from 'ui/EasTable/types';
import { saveUrl } from 'redux/reducers/auth/reducer';
import { getUsersList } from 'redux/reducers/user/selectors';
import { TUser } from 'redux/reducers/user/types';
import { GpsrRegisterDialog } from './GpsrRegisterDialog';
import { gpsrTableStructure } from './data/gpsr-table-structure';
import { COLORS } from '../../services/constants/colors';
import { EasTableWithFilter } from '../../ui/EasTableWithFilter';

export const Gpsr: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);

	const { page } = queryString.parse(search);

	const [openRegisterDialog, setOpenRegisterDialog] = useState(false);

	const items = useSelector(getGpsrItems);
	const itemsIsLoading = useSelector(getGpsrItemsLoad);
	const usersList = useSelector(getUsersList) as TUser[];
	const registerError = useSelector(getRegisterError);
	const serviceStatuses = useSelector(getGpsrServiceStatuses);
	const serviceStatusesIsLoading = useSelector(getGpsrServiceStatusesLoad);

	const tableStructure: TTableStructure<keyof TGpsrItem>[] = gpsrTableStructure({
		usersList,
		serviceStatuses: serviceStatuses ?? [],
	});

	const setCloseRegisterDialog = () => {
		setOpenRegisterDialog(false);
	};

	const handleOpenRegisterDialog = () => {
		setOpenRegisterDialog(true);
	};

	const navigateParamsWithoutPage = tableStructure.reduce((acc, item) => {
		if (!!navigateParams[item.selector] && item.type === 'date') {
			const dates = DATES_ARRAY.find((i) => i.id === navigateParams[item.selector]);
			// console.log('dates', dates);
			if (dates?.from) {
				acc.push({
					fieldName: item.selector,
					value: dates.from,
					operator: '>=',
				});
			}
			if (dates?.to) {
				acc.push({
					fieldName: item.selector,
					value: dates.to,
					operator: '<=',
				});
			}
			return acc;
		}
		if (navigateParams[item.selector])
			acc.push({
				fieldName: item.selector,
				value: navigateParams[item.selector] as string,
				operator: '=',
			});
		return acc;
	}, [] as TFilters);

	useEffect(() => {
		const orderBy = [];
		if (navigateParams?.sortIndex) {
			orderBy.push({
				fieldName: navigateParams?.sortIndex
					? tableStructure[+navigateParams.sortIndex].selector
					: 'id',
				direction: navigateParams?.sortDirection
					? String(navigateParams.sortDirection) === 'desc'
						? 'desc'
						: 'asc'
					: 'desc',
			} as TOrderBy);
		}
		orderBy.push({
			fieldName: 'id',
			direction: 'desc',
		} as TOrderBy);
		dispatch(
			getGpsrItemsRequest({
				pageNumber: page ? +page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(navigateParams?.search ?? ''),
				filters: navigateParamsWithoutPage,
				orderBy,
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search]);

	const handleLink = (id: number) => {
		dispatch(saveUrl(window.location.pathname + window.location.search));
		navigate(`/gpsr/${id}`);
	};

	useEffect(() => {
		dispatch(getGpsrServiceStatusesRequest());
	}, [dispatch]);

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<EasTableWithFilter<TGpsrItem>
				tableStructure={tableStructure}
				items={items}
				itemsIsLoading={itemsIsLoading}
				handleAction={() => {}}
				handleLink={handleLink}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Box>
						<Typography variant="body1" color="primary" fontWeight="bold">
							GPSR
						</Typography>
						{registerError && (
							<Typography
								sx={{
									fontSize: '1rem',
									color: 'error.main',
								}}
							>
								- {registerError}
							</Typography>
						)}
					</Box>
					<Button type="button" variant="contained" size="small" onClick={handleOpenRegisterDialog}>
						Add Customer
					</Button>
				</Box>
			</EasTableWithFilter>

			<GpsrRegisterDialog open={openRegisterDialog} setClose={setCloseRegisterDialog} />
		</Box>
	);
};
